import React, { useState, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import { ResourceRequester } from "sulu-admin-bundle/services/index.js";
import { routeRegistry } from "sulu-admin-bundle/services/Router";

import { DropdownButton, Toggler, Divider } from "sulu-admin-bundle/components";
import Button from "sulu-admin-bundle/components/Button";
import PlayerRecapTable from "./PlayerRecapTable";
import PassportModal from "../CustomList/PassportModal";

import styles from "./player-recap-page.module.scss";
import { UrlBuilder } from "../../utils/urlBuilder";

const urlBuilder = new UrlBuilder();

const ACTIVE_STATUS = 3;
const ARCHIVED_STATUS = 5;

const PlayerRecapPage = (props) => {
    const { t } = useTranslation();
    const router = props.router;
    const playerId = router?.attributes?.id ?? props.value;

    const isStaffView = props.route?.name?.includes("staff");

    const initialSeasonId = router?.attributes?.seasonId ?? null;
    const [seasons, setSeasons] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedSeason, setSelectedSeason] = useState({
        id: initialSeasonId,
        name: t("dhb.player.recap.season"),
    });
    const [recapData, setRecapData] = useState({
        contactName: null,
        passport: null,
        isStammspieler: false,
        stammTeam: null,
        stammLeague: null,
        matches: [],
    });
    const [passportModal, setPassportModal] = useState({
        open: false,
        item: null,
        loading: false,
    });
    const passportCache = useRef({});

    useEffect(() => {
        ResourceRequester.getList("season", { flat: true, limit: 200 })
            .then((seasonsResponse) => {
                const list = seasonsResponse._embedded?.season ?? [];
                setSeasons(list);

                if (initialSeasonId) {
                    const match = list.find(
                        (s) => String(s.id) === String(initialSeasonId),
                    );
                    if (match) {
                        setSelectedSeason({ id: match.id, name: match.name });
                    }
                }
            })
            .catch((err) => console.error("Failed to load seasons", err));
    }, []);

    useEffect(() => {
        if (!playerId || !selectedSeason.id) return;
        setLoading(true);
        ResourceRequester.get("player_recap", {
            id: playerId,
            seasonId: selectedSeason.id,
        })
            .then((data) => {
                setRecapData({
                    contactName:
                        data.firstName || data.lastName
                            ? `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim()
                            : null,
                    passport:
                        data.passport && Object.keys(data.passport).length > 0
                            ? data.passport
                            : null,
                    isStammspieler: data.isStammspieler ?? false,
                    stammTeam: data.stammTeam ?? null,
                    stammLeague: data.stammLeague ?? null,
                    matches: data.playedMatches ?? [],
                });
            })
            .catch((err) => console.error("Failed to load player recap", err))
            .finally(() => setLoading(false));
    }, [playerId, selectedSeason.id]);

    const visibleSeasons = seasons.filter(
        (s) =>
            s.statusFilter === ACTIVE_STATUS ||
            (showArchived && s.statusFilter === ARCHIVED_STATUS),
    );

    const handleSeasonSelect = (season) => {
        setSelectedSeason({ id: season.id, name: season.name });
    };

    const openPassportModal = () => {
        const passportId = recapData.passport.passportId;
        const cached = passportCache.current[passportId];

        if (cached) {
            setPassportModal({ open: true, item: cached, loading: false });
            return;
        }

        setPassportModal({ open: true, item: null, loading: true });
        ResourceRequester.get("dhbpassports", {
            locale: router.attributes.locale,
            id: passportId,
        })
            .then((data) => {
                passportCache.current[passportId] = data;
                setPassportModal({ open: true, item: data, loading: false });
            })
            .catch((err) =>
                setPassportModal({
                    open: true,
                    item: { error: err.statusText },
                    loading: false,
                }),
            );
    };

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("de-DE");
    };

    return (
        <div className={styles.page}>
            {/* Top controls */}
            <div className={styles.controls}>
                <Toggler
                    checked={showArchived}
                    onChange={() => setShowArchived((v) => !v)}
                >
                    {t("dhb.player.recap.show_archived_seasons")}
                </Toggler>

                <DropdownButton label={selectedSeason.name} skin="primary">
                    {visibleSeasons.map((season) => (
                        <DropdownButton.Item
                            key={season.id}
                            onClick={() => handleSeasonSelect(season)}
                        >
                            <span
                                style={
                                    selectedSeason.id === season.id
                                        ? { fontWeight: "bold" }
                                        : {}
                                }
                            >
                                {season.name}
                            </span>
                        </DropdownButton.Item>
                    ))}
                </DropdownButton>
            </div>

            {/* Passport section — only for players, not staff */}
            {!isStaffView && recapData.passport && (
                <>
                    <Divider>{t("dhb.player.recap.player_passport")}</Divider>
                    <div className={styles.passportGrid}>
                        <div className={styles.passportField}>
                            <label>{t("dhb.player.recap.passport_type")}</label>
                            <span>{recapData.passport.passportType}</span>
                        </div>
                        <div className={styles.passportField}>
                            <label>
                                {t("dhb.player.recap.passport_number")}
                            </label>
                            <Button
                                skin="link"
                                onClick={openPassportModal}
                                className={styles.passportLinkButton}
                            >
                                {recapData.passport.passportNumber ?? "—"}
                            </Button>
                        </div>
                        <div className={styles.passportField}>
                            <label>{t("dhb.player.recap.club")}</label>
                            <span>{recapData.passport.clubName ?? "—"}</span>
                        </div>
                        <div className={styles.passportField}>
                            <label>{t("dhb.player.recap.date_of_birth")}</label>
                            <span>
                                {formatDate(recapData.passport.birthDate)}
                            </span>
                        </div>
                        <div className={styles.passportField}>
                            <label>{t("dhb.player.recap.first_name")}</label>
                            <span>{recapData.passport.firstName ?? "—"}</span>
                        </div>
                        <div className={styles.passportField}>
                            <label>{t("dhb.player.recap.last_name")}</label>
                            <span>{recapData.passport.lastName ?? "—"}</span>
                        </div>
                    </div>
                </>
            )}

            {/* Matches section */}
            <Divider>
                {isStaffView && recapData.contactName
                    ? recapData.contactName
                    : t("dhb.player.recap.played_matches")}
            </Divider>
            {recapData.isStammspieler && !loading && (
                <div className={styles.stammspielerBanner}>
                    <span className={styles.stammspielerIcon}>★</span>
                    <span>{t("dhb.player.recap.stammspieler")}</span>
                    {recapData.stammTeam && (
                        <span className={styles.stammspielerTeam}>
                            {recapData.stammTeam}
                            {recapData.stammLeague
                                ? ` · ${recapData.stammLeague}`
                                : ""}
                        </span>
                    )}
                </div>
            )}
            <PlayerRecapTable
                data={recapData.matches}
                loading={loading}
                onMatchClick={(matchId) => {
                    const route =
                        routeRegistry.getAll()["app.dhbmatches_edit_form"];
                    if (!route) return;
                    const url = urlBuilder.generateUrl(route.path, {
                        locale: router.attributes.locale,
                        id: matchId,
                    });
                    window.open(url, "_blank");
                }}
            />

            <PassportModal
                isOpen={passportModal.open}
                closeHandler={() =>
                    setPassportModal({
                        open: false,
                        item: null,
                        loading: false,
                    })
                }
                passportItem={passportModal.item}
                loading={passportModal.loading}
            />
        </div>
    );
};

export default PlayerRecapPage;
